import { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";

const PLANS = [
  {
    name: "Free",
    free: true,
    monthly: 0,
    yearly: 0,
    link: "https://breel.ink/pay/free",
    features: ["1 BREELINK page", "Unlimited links", "Core content blocks", "BREELINK domain"],
    cta: "Start free",
    featured: false,
  },
  {
    name: "Starter",
    monthly: 3,
    yearly: 30,
    link: "/pay/1",
    features: ["Everything in Free", "Custom themes", "Basic analytics", "Priority uptime"],
    cta: "Choose Starter",
    featured: false,
  },
  {
    name: "Pro",
    monthly: 6,
    yearly: 60,
    link: "/pay/2",
    features: [
      "Everything in Starter",
      "Custom domain",
      "Advanced analytics",
      "All premium blocks",
      "Remove BREELINK badge",
    ],
    cta: "Choose Pro",
    featured: true,
  },
  {
    name: "Premium",
    monthly: 20,
    yearly: 220,
    link: "/pay/3",
    features: ["Everything in Pro", "Priority support", "Team seats", "API access", "Early access features"],
    cta: "Choose Premium",
    featured: false,
  },
];

const LIFETIME = {
  name: "Lifetime",
  price: 499,
  link: "/pay/3",
  features: ["Everything in Premium", "One-time payment", "Lifetime updates", "Founder badge"],
};

const monthlyEquivalent = (yearly) => {
  const v = yearly / 12;
  return Number.isInteger(v) ? `${v}` : v.toFixed(2);
};

export const Pricing = () => {
  const [yearly, setYearly] = useState(true);

  return (
    <section
      id="pricing"
      data-testid="pricing"
      className="border-t border-line bg-surfaceAlt px-6 py-28 md:px-12 lg:px-24"
    >
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-inkMuted">Pricing</p>
          <div className="mt-4 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <h2 className="max-w-2xl font-display text-4xl font-semibold tracking-tighter text-ink md:text-5xl lg:text-6xl">
              Simple pricing that scales with you
            </h2>

            {/* billing toggle */}
            <div
              data-testid="billing-toggle"
              className="inline-flex items-center rounded-full border border-line bg-surface p-1 shadow-soft"
            >
              <button
                type="button"
                data-testid="billing-toggle-monthly"
                onClick={() => setYearly(false)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  !yearly ? "bg-lime text-limeText" : "text-inkMuted hover:text-ink"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                data-testid="billing-toggle-yearly"
                onClick={() => setYearly(true)}
                className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  yearly ? "bg-lime text-limeText" : "text-inkMuted hover:text-ink"
                }`}
              >
                Yearly
                <span className={`text-[10px] font-bold uppercase tracking-wide ${yearly ? "text-limeText/70" : "text-lime"}`}>
                  Save
                </span>
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p, i) => {
            const price = yearly ? p.yearly : p.monthly;
            const period = p.free ? "/forever" : yearly ? "/year" : "/month";
            const save = p.monthly * 12 - p.yearly;
            const note = p.free
              ? "No credit card required"
              : yearly
              ? `≈ $${monthlyEquivalent(p.yearly)}/mo · save $${save}/yr`
              : "billed monthly";
            return (
              <Reveal key={p.name} delay={i * 0.08}>
                <div
                  data-testid={`pricing-plan-${p.name.toLowerCase()}`}
                  className={`flex h-full flex-col rounded-3xl border p-8 transition-transform duration-300 hover:-translate-y-1 ${
                    p.featured
                      ? "border-lime bg-ink text-bg shadow-lift"
                      : "border-line bg-surface text-ink shadow-soft"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold uppercase tracking-widest ${p.featured ? "text-lime" : "text-inkMuted"}`}>
                      {p.name}
                    </span>
                    {p.featured && (
                      <span className="rounded-full bg-lime px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-limeText">
                        Popular
                      </span>
                    )}
                  </div>

                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-display text-5xl font-semibold tracking-tighter">${price}</span>
                    <span className={`mb-1.5 text-sm ${p.featured ? "text-bg/60" : "text-inkMuted"}`}>{period}</span>
                  </div>
                  <p className={`mt-1.5 text-xs font-medium ${p.featured ? "text-bg/60" : "text-inkMuted"}`}>
                    {note}
                  </p>

                  <ul className="mt-7 flex-1 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm">
                        <Check size={15} className="shrink-0 text-lime" />
                        <span className={p.featured ? "text-bg/85" : "text-inkMuted"}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={p.link}
                    data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                    className={`mt-8 inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                      p.featured ? "bg-lime text-limeText" : "border border-line bg-surfaceAlt text-ink"
                    }`}
                  >
                    {p.cta}
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Lifetime banner */}
        <Reveal delay={0.1}>
          <div
            data-testid="pricing-plan-lifetime"
            className="mt-5 flex flex-col items-start justify-between gap-6 rounded-3xl border border-line bg-surface p-8 shadow-soft md:flex-row md:items-center"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-lime" />
                <span className="text-sm font-semibold uppercase tracking-widest text-inkMuted">Lifetime</span>
                <span className="rounded-full bg-lime px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-limeText">
                  Best value
                </span>
              </div>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-5xl font-semibold tracking-tighter text-ink">${LIFETIME.price}</span>
                <span className="mb-1.5 text-sm text-inkMuted">one-time</span>
              </div>
              <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {LIFETIME.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-inkMuted">
                    <Check size={15} className="shrink-0 text-lime" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <a
              href={LIFETIME.link}
              data-testid="pricing-cta-lifetime"
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-ink px-8 py-4 text-sm font-semibold text-bg transition-transform hover:-translate-y-0.5"
            >
              Get Lifetime access
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
